import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { Label } from '../../../../../../../CommonComponents/OPL/Label/Label';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { SingleDatePicker } from '../../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { dateToString } from '../../../../../../../CommonComponents/OPL/dates/utils';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { GranTypeDTO } from '../../../../../../../Profile/Accounts/api/useAccountsQuery';
import { OptionsPerGrantTypeDTO } from '../../../../../api/useStockOptionsQuery';
import { FormValues } from '../../../UpsertGrantModal.meta';
import { FieldsGrid } from '../FieldsGrid';

type DispositionExerciseOption = OptionsPerGrantTypeDTO['AvailableDispositionOptions'][number];

interface Props {
  grantType: GranTypeDTO;
  dispositionOptions: DispositionExerciseOption[] | null;
}

export const OwnedSharesForm = ({ grantType, dispositionOptions }: Props) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const ownedDispositionStrategy = watch('ownedDispositionStrategy');
  const selectedOwnedDisposition = dispositionOptions
    ? dispositionOptions.find(opt => opt.Value === ownedDispositionStrategy)
    : null;

  return (
    <Box pt="8">
      <Text color="neutral.500" mb="12" fontSize="small">
        Use this section to model shares that have been exercised but not yet sold. Identifying
        these shares as equity compensation preserves the correct tax treatment upon disposition.
      </Text>
      <FieldsGrid>
        <Controller
          control={control}
          name="ownedShares"
          render={({ field: { value, onChange, ...field } }) => (
            <InputNumberMask
              label="Shares Owned"
              {...field}
              value={value}
              allowNegative={false}
              thousandSeparator={false}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              formVariant={errors?.ownedShares ? 'error' : undefined}
            />
          )}
        />
        <Box>
          <Label
            htmlFor="ownedExerciseDate"
            mb="4"
            mt="2"
            variant={errors?.ownedExerciseDate ? 'error' : undefined}
          >
            Exercise Date
          </Label>
          <Controller
            control={control}
            name="ownedExerciseDate"
            render={({ field: { value, onChange, ref } }) => (
              <SingleDatePicker
                id="ownedExerciseDate"
                inputRef={ref}
                date={value ? new Date(value) : undefined}
                onChange={date => onChange(date ? dateToString(date) : null)}
                maxDate={new Date()}
                isWrapperWidth
                formVariant={errors?.ownedExerciseDate ? 'error' : undefined}
              />
            )}
          />
        </Box>
        <Controller
          control={control}
          name="ownedStrikePrice"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Strike Price"
              rightIcon="far fa-dollar-sign"
              decimalScale={2}
              allowNegative={false}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              {...field}
              formVariant={errors?.ownedStrikePrice ? 'error' : undefined}
            />
          )}
        />
        <Controller
          control={control}
          name="ownedFmvAtExercise"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Price Per Share at Exercise"
              rightIcon="far fa-dollar-sign"
              decimalScale={2}
              allowNegative={false}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              {...field}
              formVariant={errors?.ownedFmvAtExercise ? 'error' : undefined}
            />
          )}
        />
        <Box>
          <Box display="flex" alignItems="center" mb="4" mt="2">
            <Label mb="0">Bargain Element</Label>
            <Tooltip
              content={
                grantType === GranTypeDTO.Nso
                  ? 'The bargain element is the difference between the price per share at exercise and the strike price. For NSOs, this amount was taxed as ordinary income when the shares were exercised. Any additional gain upon disposition is taxed as capital gains.'
                  : 'The bargain element is the difference between the price per share at exercise and the strike price. For ISOs, this amount is taxed as a long-term capital gain when shares are sold, as long as the disposition is qualifying.'
              }
              placement="top"
            >
              <Icon className="far fa-info-circle" color="neutral.500" fontSize="small" ml="4" />
            </Tooltip>
          </Box>
          <Input
            value={`$${Math.max(
              0,
              (watch('ownedFmvAtExercise') ?? 0) - (watch('ownedStrikePrice') ?? 0),
            ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            disabled
            aria-label="Bargain element"
          />
        </Box>
        {dispositionOptions && (
          <>
            <Controller
              name="ownedDispositionStrategy"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  isClearable
                  innerRef={ref}
                  label="Disposition Strategy"
                  value={
                    value !== null
                      ? {
                          value,
                          label: dispositionOptions.find(opt => opt.Value === value)?.Label,
                        }
                      : null
                  }
                  options={dispositionOptions.map(opt => ({
                    value: opt.Value,
                    label: opt.Label,
                  }))}
                  onChange={option => {
                    onChange(option?.value ?? null);
                    if (!option) {
                      setValue('ownedDispositionDate', null);
                      setValue('ownedDispositionYear', null);
                    }
                  }}
                  isPortal
                />
              )}
            />
            {selectedOwnedDisposition?.ShowDateSelector && (
              <Box>
                <Label htmlFor="ownedDispositionDate" mb="4">
                  Disposition Date
                </Label>
                <Controller
                  control={control}
                  name="ownedDispositionDate"
                  render={({ field: { value, onChange, ref } }) => (
                    <SingleDatePicker
                      id="ownedDispositionDate"
                      inputRef={ref}
                      date={value ? new Date(value) : undefined}
                      onChange={date => onChange(date ? dateToString(date) : null)}
                      isWrapperWidth
                    />
                  )}
                />
              </Box>
            )}
            {selectedOwnedDisposition?.ShowYearSelector && (
              <Controller
                control={control}
                name="ownedDispositionYear"
                render={({ field: { onChange, ...field } }) => (
                  <InputNumberMask
                    label="Years Until Disposition"
                    decimalScale={0}
                    maxLength={2}
                    thousandSeparator={false}
                    allowNegative={false}
                    onValueChange={({ floatValue }) => onChange(floatValue || '')}
                    {...field}
                  />
                )}
              />
            )}
          </>
        )}
      </FieldsGrid>
    </Box>
  );
};
